import { env } from '@noor/config';
import type { SubApp, SubAppId } from '../model/types';

/**
 * Legacy Eats availability flag helper. The launcher no longer uses this gate:
 * Eats is always navigable, and production can show only the independent
 * "Скоро" badge via `VITE_EATS_SOON_BADGE`.
 */
export function isEatsSoon(eatsEnabled: boolean = env.VITE_EATS_ENABLED): boolean {
  return !eatsEnabled;
}

/**
 * Whether Eats carries a "Soon" tag while open. Only meaningful when Eats is
 * navigable — Eats is always navigable in the launcher.
 */
export function isEatsBadged(
  badge: boolean = env.VITE_EATS_SOON_BADGE,
  soon = false,
): boolean {
  return badge && !soon;
}

const EATS_BADGED = isEatsBadged();

/**
 * The sub-app catalog — **edit this list to control the launcher.**
 *
 * Array order is display order, on the welcome picker and in the header
 * switcher alike. To ship a sub-app: give it a `path` and flip `status` to
 * `'available'`. To tease one: leave it `'soon'`. To pull one entirely (staging
 * behind a flag, or an entitlement the merchant lacks): set `'hidden'` — nothing
 * else in the app needs to change.
 *
 * Eats is always an available destination. `VITE_EATS_SOON_BADGE=true` adds
 * the visual "Скоро" tag without disabling navigation.
 *
 * Labels and blurbs are **not** here: they live in `common:subApps.<id>` and
 * `common:welcome.blurbs.<id>` so all three locales stay in one place. See
 * `entities/sub-app/model/types.ts`.
 *
 * `artClassName` is the per-card placement of the 3D product shot, transcribed
 * from the Figma frames (node 838:14191). Each shot is positioned differently —
 * some bleed off an edge, some are rotated, one is scaled past its card — so the
 * geometry is data here rather than one rule in the card component. Sizes are
 * percentages of the card box (284px tall, width per `span`), which is what lets
 * the same numbers hold as the grid goes fluid below the design's 1440px.
 */
export const SUB_APPS: readonly SubApp[] = [
  {
    id: 'delivery',
    path: '/delivery',
    status: 'available',
    icon: '/app-switcher/delivery.png',
    art: '/sub-apps/delivery.png',
    // Figma 963:10083 — 356×267 at x=447 (of 803), y=17. Bottom-aligned.
    artClassName:
      'lg:right-0 lg:top-[6%] lg:h-[94%] lg:w-[44.35%] lg:object-cover lg:object-center lg:bottom-auto lg:left-auto',
  },
  {
    id: 'eats',
    status: 'available',
    path: '/eats',
    ...(EATS_BADGED ? { badge: 'soon' as const } : {}),
    icon: '/app-switcher/food.png',
    art: '/sub-apps/eats.png',
    // Figma 963:10085 — 228×175 flush to the bottom-right corner (of 389).
    artClassName:
      'lg:right-0 lg:bottom-0 lg:h-[61.62%] lg:w-[58.61%] lg:object-cover lg:object-[16.3%_0%] lg:left-auto lg:top-auto',
  },
  {
    id: 'shop',
    status: 'soon',
    icon: '/app-switcher/shop.png',
    art: '/sub-apps/shop.png',
    // Figma 963:10087 — 274×274 at x=183, y=69, overflowing the card's bottom
    // and right edges (the card clips it).
    artClassName:
      'lg:left-[47.04%] lg:top-[24.3%] lg:h-[96.48%] lg:w-[70.44%] lg:object-cover lg:object-center lg:right-auto lg:bottom-auto',
  },
  {
    id: 'gifts',
    status: 'soon',
    icon: '/app-switcher/shop.png',
    art: '/sub-apps/gifts.png',
    // Figma 963:10091 — the only card whose frame is NOT usable as written: it
    // sits at x=429 on a 390-wide card (fully past the right edge) and a nested
    // rotate-180 + -scale-y-100 flip mirrors the bottle back into view, which
    // does not survive being flattened to one <img>. Solved from the RENDERED
    // card instead — the gold bottle starts at (276,125) of 390×284 and is
    // clipped on the right and bottom — keeping the frame's own 183×219 size.
    artClassName:
      'lg:left-[66%] lg:top-[32.22%] lg:h-[85%] lg:w-[52%] lg:object-cover lg:right-auto lg:bottom-auto',
  },
  {
    id: 'flowers',
    status: 'soon',
    icon: '/app-switcher/shop.png',
    art: '/sub-apps/flowers.png',
    // Figma 963:10096 — 246×175 at x=143, flush to the bottom-right (of 389).
    artClassName:
      'lg:right-0 lg:bottom-0 lg:h-[61.62%] lg:w-[63.24%] lg:object-cover lg:object-bottom lg:left-auto lg:top-auto',
  },
  {
    id: 'charging',
    status: 'soon',
    icon: '/app-switcher/shop.png',
    art: '/sub-apps/charging.png',
    // Figma 965:10102 — a 268×338 window at x=328 (of 596), y=24, running 78px
    // past the card's bottom edge. The source held a row of five chargers and
    // the frame cropped to the middle one; `charging.png` is committed
    // pre-cropped to that slice, so it renders as a plain top-anchored shot.
    artClassName:
      'lg:left-[55.03%] lg:top-[8.45%] lg:h-[119.01%] lg:w-[44.97%] lg:object-cover lg:object-center lg:right-auto lg:bottom-auto',
  },
  {
    id: 'market',
    status: 'soon',
    icon: '/app-switcher/shop.png',
    art: '/sub-apps/market.png',
    // Figma 965:10186 — a 309×309 shot rotated -7.02°. The frame's 344×344 box
    // is the ROTATED bounding box (at x=308, y=83 of the 596×284 card), so the
    // un-rotated image is its centre inset by (344-309)/2 — hence 54.65%/35.37%
    // rather than the box's own origin. Overflows the right and bottom edges.
    artClassName:
      'lg:left-[54.65%] lg:top-[35.37%] lg:h-[108.8%] lg:w-[51.85%] lg:-rotate-[7.02deg] lg:object-cover lg:object-center lg:right-auto lg:bottom-auto',
  },
] as const;

/**
 * The sub-app pre-selected on the welcome launcher, and the fallback landing
 * target when a merchant arrives without choosing one. Matches the Figma, which
 * leads with Доставка.
 */
export const DEFAULT_SUB_APP_ID: SubAppId = 'delivery';

/**
 * How many grid columns each card spans on the desktop bento layout, keyed by
 * id. The Figma lays 7 cards into three rows on a 1216px, 24px-gutter grid:
 *
 * ```
 * ┌──────────── Доставка (803) ────────────┬── Еда (389) ──┐
 * ├── Магазин (389) ──┬── Подарки (390) ───┼── Цветы (389)─┤
 * ├──── Электростанция (596) ──────────────┴─ Лавка (596) ─┤
 * ```
 *
 * Those widths are 2:1 and 1:1:1 and 1:1 splits of the same row, which a 6-track
 * grid expresses exactly: 4+2, 2+2+2, 3+3. Anything not listed spans 2 — a new
 * `soon` sub-app slots into the middle row's rhythm without a layout change.
 */
export const SUB_APP_SPANS: Partial<Record<SubAppId, number>> = {
  delivery: 4,
  eats: 2,
  shop: 2,
  gifts: 2,
  flowers: 2,
  charging: 3,
  market: 3,
};
